// --- Sentry Error Tracking ---
if (process.env.REACT_APP_SENTRY_DSN) {
  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_ENVIRONMENT || 'production',
      tracesSampleRate: 0.1,
    });
  });
}

import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";

// Utility to convert VAPID public key string into a Uint8Array for the browser
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Register service worker for PWA
// NOTE: we intentionally use /sw.js (our clean, no-API-cache SW) instead of
// /service-worker.js (which react-scripts auto-generates via workbox and caches /api/* for 24h).
// On load we also unregister any lingering /service-worker.js registration from older builds.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Purge lingering old workbox SW + its caches (one-time migration)
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => {
        if (r.active && r.active.scriptURL && r.active.scriptURL.endsWith('/service-worker.js')) {
          r.unregister().then(() => {
            if ('caches' in window) {
              caches.keys().then((names) => {
                names.forEach((n) => {
                  if (n.startsWith('workbox') || n === 'api-responses' || n === 'media-cache') {
                    caches.delete(n);
                  }
                });
              });
            }
          });
        }
      });
    });

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('ServiceWorker registered:', registration.scope);

        // Request notification permission for Push Readiness
        if ('Notification' in window) {
          Notification.requestPermission().then(async permission => {
            if (permission === 'granted') {
              console.log('Notification permission granted.');

              try {
                // Check if user is logged in before we try to save a subscription to their profile
                const token = localStorage.getItem('token');
                if (!token) return; // Silent return, we'll try again next time they log in/reload

                // Fetch VAPID key securely from backend
                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
                const vapidResponse = await axios.get(`${backendUrl}/api/notifications/vapid-public-key`, {
                  headers: { Authorization: `Bearer ${token}` }
                });

                const applicationServerKey = urlBase64ToUint8Array(vapidResponse.data.public_key);

                // Ask the browser native Push Manager to generate us a unique encrypted subscription
                const subscription = await registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: applicationServerKey
                });

                // Send the generated subscription up to our FastAPI MongoDB database
                const backendUrl2 = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
                await axios.post(`${backendUrl2}/api/notifications/subscribe`, subscription, {
                  headers: { Authorization: `Bearer ${token}` }
                });

                console.log('Push subscription successfully saved to backend!');

              } catch (subError) {
                console.error("Failed to subscribe the browser to VAPID Web Push:", subError);
              }
            }
          });
        }
      })
      .catch((err) => {
        console.error('ServiceWorker registration failed:', err);
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
